import { debounce } from "lodash";
import { makeAutoObservable } from "mobx";
import { downloadFromUrl } from "../../../common/utils/network.utils";
import { errorNotification, successNotification, successNotificationWithUndo } from "../../layout/components/Notifications/notifications";
import { bulkDeleteFiles, bulkDownloadUrls, bulkUpdateMetadata, confirmVaults, deleteVaultFile, downloadVaultFile, fetchUserPeriods, fetchUserTags, fetchVaultById, fetchVaultMetadata, getVaultsFormStep, search, updateVaultMetadata, upload } from "./vault.service";

const PAGE_SIZE = 25;
export const SORT_BY_OPTIONS = [
    { label: 'Last uploaded', value: 'lastUploaded', isOpposite: true },
    { label: 'Last modified', value: 'lastModified', isOpposite: true },
    { label: 'Name', value: 'name' },
    { label: 'File size', value: 'fileSize' }
];

export const PREDEFINED_TAG_NAMES = {
    'subscription_documents': 'Subscription Documents',
    'liabilities_documents': 'Liabilities Documents',
    'account_statements': 'Account Statements',
    'k1': 'K1',
    'form_1099': 'Form 1099',
    'tickers': 'Tickers',
    'balance_sheet': 'Balance Sheet',
    'other': 'Other'
};

const normalizeTag = (tag) => {
    if (typeof tag === 'string') {
        return {
            id: tag,
            tag: tag,
            name: tag,
            label: tag
        };
    }

    return {
        id: tag.id || tag.tag || tag.name,
        tag: tag.tag || tag.name || tag.id,
        name: tag.name || tag.tag || tag.id,
        label: tag.label || tag.name || tag.tag || tag.id
    };
};

const normalizePeriod = (period) => ({
    id: period.id || period.period || period.key,
    key: period.key || period.id,
    label: period.label || period.period,
    period: period.period || period.label,
    type: period.type || 'period'
});

const normalizeVaultData = (vault) => ({
    ...vault,
    tags: vault.tags?.map(tag => normalizeTag(typeof tag === 'object' && tag.tag ? tag : tag)) || [],
    periods: vault.periods?.map(period => normalizePeriod(typeof period === 'object' && period.period ? period : period)) || [],
    linkedAssets: vault.linkedAssets || [],
    relatedItems: vault.linkedAssets?.map(linkedAsset => ({
        id: linkedAsset.asset?.id || linkedAsset.assetId,
        name: linkedAsset.asset?.title || linkedAsset.asset?.name || 'Unknown Asset',
        avatar: (linkedAsset.asset?.title || linkedAsset.asset?.name || 'U').charAt(0).toUpperCase(),
        type: 'asset',
        logoUrl: linkedAsset.asset?.logoUrl || null,
        logoBase64: linkedAsset.asset?.logoBase64 || null,
        categoryId: linkedAsset.asset?.categoryId,
        title: linkedAsset.asset?.title,
    })) || []
});

const compareItems = (item1, item2, properties) =>
    properties.some(prop => item1[prop] === item2[prop]);

const transformPeriodsFromBackend = (backendPeriods, hasNoPeriodFiles = false) => {
    if (!Array.isArray(backendPeriods)) return [];

    const yearMap = new Map();

    backendPeriods.forEach(periodItem => {
        const periodValue = typeof periodItem === 'string' ? periodItem : (periodItem.period || periodItem);
        if (!periodValue) return;

        const periodStr = String(periodValue).trim();
        if (!periodStr) return;

        const quarterYearMatch = periodStr.match(/^Q([1-4])\s+(\d{4})$/);
        const yearQuarterMatch = periodStr.match(/^(\d{4})-Q([1-4])$/);
        const yearOnlyMatch = periodStr.match(/^(\d{4})$/);

        if (quarterYearMatch || yearQuarterMatch) {
            const [, quarter, year] = quarterYearMatch || [null, yearQuarterMatch[2], yearQuarterMatch[1]];
            const quarterKey = `${year}-Q${quarter}`;
            const quarterLabel = `Q${quarter} ${year}`;

            if (!yearMap.has(year)) {
                yearMap.set(year, {
                    id: year, key: year, label: year, period: year, type: 'year', quarters: [], hasDirectFiles: false
                });
            }

            yearMap.get(year).quarters.push({
                id: quarterLabel, key: quarterKey, label: quarterLabel,
                period: quarterLabel, type: 'quarter'
            });
        } else if (yearOnlyMatch) {
            const year = yearOnlyMatch[1];
            if (!yearMap.has(year)) {
                yearMap.set(year, {
                    id: year, key: year, label: year, period: year, type: 'year', quarters: [], hasDirectFiles: true
                });
            } else {
                yearMap.get(year).hasDirectFiles = true;
            }
        } else {
            const periodKey = periodStr.toLowerCase().replace(/\s+/g, '-');
            yearMap.set(periodKey, {
                id: periodKey, key: periodKey, label: periodStr,
                period: periodStr, type: 'period', quarters: []
            });
        }
    });

    if (hasNoPeriodFiles) {
        yearMap.set('no-period-files', {
            id: 'no-period-files',
            key: 'no-period-files',
            label: 'No Period Files',
            period: 'no-period-files',
            type: 'no-period-files',
            quarters: []
        });
    }

    return Array.from(yearMap.values()).sort((a, b) => {
        if (a.type === 'no-period-files') return -1;
        if (b.type === 'no-period-files') return 1;
        if (a.type === 'year' && b.type === 'year') return parseInt(b.key) - parseInt(a.key);
        if (a.type === 'year') return -1;
        if (b.type === 'year') return 1;
        return a.label.localeCompare(b.label);
    });
};

export class VaultStore {
    isLoading = false;
    isUploading = false;
    files = [];
    sortBy = 0;
    sortDirection = 1;
    searchValue = '';
    page = 1;
    meta = 0;
    periodsExpandedThreshold = 20;
    mainTableWidth = null;

    activeFilters = ['all-files'];
    selectedTags = [];
    selectedStatuses = [];
    selectedPeriods = [];
    assetId = null;

    selectedFile = null;
    vaultMetadata = { tags: [], periods: [] };
    userTags = [];
    userPeriods = [];

    isLoadingVaultDetails = false;
    isLoadingMetadata = false;
    isLoadingTags = false;
    isLoadingPeriods = false;
    vaultFormStep = null;
    selectedFilesForBulkEdit = [];
    selectedAttributeToEdit = null;
    valuesForBulkEditUndoAction = [];
    numberOfBulkEditUpdatedItems = 0;
    isLoadingBulkUpdate = false;
    isLoadingBulkDelete = false;
    hasNoPeriodFiles = false;
    isUpdatingAssetId = false;

    constructor(metadataStore) {
        makeAutoObservable(this);
        this.metadataStore = metadataStore;
    }

    debouncedRefetch = debounce(this.refetchFiles.bind(this), 300);

    filesData = null;


    async fetchFiles() {
        try {
            this.isLoading = true;

            const name = this.searchValue?.trim();
            const tags = this.selectedTags.map(tag => tag.tag || tag.name || tag.id);
            const periods = this.selectedPeriods.map(period => {
                if (period.type === 'no-period-files') return 'no-period-files';
                if (period.type === 'year') return period.key || period.label || period.period;
                if (period.type === 'quarter' && period.label) return period.label;
                return period.period || period.label || period.key;
            });

            const sortBy = SORT_BY_OPTIONS[this.sortBy]?.value;
            const sortOrder = (this.sortDirection === 1)
                ? (SORT_BY_OPTIONS[this.sortBy]?.isOpposite ? 'DESC' : 'ASC')
                : (SORT_BY_OPTIONS[this.sortBy]?.isOpposite ? 'ASC' : 'DESC');
            const status = ((1 === this.selectedStatuses.length) ? this.selectedStatuses[0].id : undefined);
            const page = this.page;

            const data = await search({
                name,
                tags,
                periods,
                status,
                sortBy,
                sortOrder,
                page,
                ...(this.isAssetVaultTab ? { assetId: this.assetId } : {}),
            });

            const existingIds = new Set(this.files.map(f => f.id));
            const newFiles = data.items.filter(file => !existingIds.has(file.id)).map(normalizeVaultData);
            this.files.push(...newFiles);
            this.setMeta(data.meta);
            this.hasNoPeriodFiles = data.hasNoPeriodFiles || false;
            this.refreshPeriodsIfNeeded();

        } catch (error) {
            console.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    async quickSearch(searchValue) {
        const data = await search({ name: searchValue, page: 1, limit: 3, assetId: this.assetId });
        return data.items;
    }

    async upload(acceptedFiles, rejectedFiles, options) {
        if (this.isUploading) return;
        const { directUpload = false } = (options || {});

        const hasAcceptedFiles = (acceptedFiles && acceptedFiles.length > 0);
        if (!hasAcceptedFiles) {
            this.setFilesData({ errors: rejectedFiles[0].errors });
            this.setVaultFormStep('fail');
            return;
        }

        this.isUploading = true;
        try {
            const formData = new FormData();
            acceptedFiles.forEach(file => {
                formData.append('files', file);
            });

            const response = await upload(formData, directUpload);
            if (201 === response.statusCode) {
                if (directUpload) {
                    const vaults = response.data;
                    await confirmVaults(vaults.map(vault => vault.id));
                    this.setFilesData({ numOfItems: acceptedFiles.length, singleFileName: acceptedFiles[0].name });
                    this.setVaultFormStep('deep-analysis');
                } else {
                    await this.fetchVaultsFormStep();
                }
            }
        } catch (error) {
            console.error('Error uploading documents:', error);
            console.log(acceptedFiles);
        } finally {
            this.isUploading = false;
        }
    }

    setFilters(filters) {
        if (this.filters.every(filter => filters.includes(filter))) {
            return;
        }

        this.filters = filters;
        this.refetchFiles();
    }

    async resetAndRefetchFiles() {
        this.reset();
        await this.fetchFiles();
    }

    reset() {
        this.page = 1;
        this.searchValue = '';
        this.setSelectedTags([]);
        this.setSelectedPeriods([]);
        this.setSelectedStatuses([]);
        this.setFiles([]);
    }

    async refetchFiles() {
        this.isLoading = true;
        this.page = 1;
        this.setFiles([]);
        await this.fetchFiles();
        this.selectedFilesForBulkEdit = this.selectedFilesForBulkEdit.filter(id => this.files.some(f => f.id === id));
    }

    async goToPage(page) {
        if (page === this.page || page < 1 || page > this.meta.totalPages) return;
        this.page = page;
        this.selectedFilesForBulkEdit = [];
        if (!this.isPageFetched(page)) await this.fetchFiles();
    }

    async fetchVaultsFormStep() {
        const data = await getVaultsFormStep();
        this.setVaultFormStep(data);
    }

    isPageFetched(page) {
        return this.files.length > ((page - 1) * PAGE_SIZE);
    }

    setSortBy(sortBy) {
        if (this.sortBy !== sortBy) {
            this.sortBy = sortBy;
            this.debouncedRefetch();
        }
    }

    setSortDirection(sortDirection) {
        if (this.sortDirection !== sortDirection) {
            this.sortDirection = sortDirection;
            this.debouncedRefetch();
        }
    }

    setSearchValue(searchValue) {
        if (this.searchValue !== searchValue) {
            this.searchValue = searchValue;
            this.debouncedRefetch();
        }
    }

    clearSearchValue() {
        this.searchValue = '';
        this.refetchFiles();
    }

    toggleTag(tag) {
        const existingIndex = this.selectedTags.findIndex(t =>
            compareItems(t, tag, ['id', 'tag', 'name'])
        );

        if (existingIndex >= 0) {
            this.selectedTags = this.selectedTags.filter((t, index) => index !== existingIndex);
        } else {
            this.selectedTags = [...this.selectedTags, normalizeTag(tag)];
        }
        this.debouncedRefetch();
    }

    toggleStatus(statusId, statusLabel) {
        const existingIndex = this.selectedStatuses.findIndex(s => s.id === statusId);
        if (existingIndex >= 0) {
            this.selectedStatuses = this.selectedStatuses.filter(s => s.id !== statusId);
        } else {
            this.selectedStatuses = [...this.selectedStatuses, { id: statusId, label: statusLabel }];
        }
        this.debouncedRefetch();
    }

    togglePeriod(period) {
        const existingIndex = this.selectedPeriods.findIndex(p =>
            compareItems(p, period, ['key', 'id', 'period', 'label'])
        );

        if (existingIndex >= 0) {
            this.selectedPeriods = this.selectedPeriods.filter((p, index) => index !== existingIndex);
        } else {
            this.selectedPeriods = [...this.selectedPeriods, period];
        }
        this.debouncedRefetch();
    }

    clearSelectedTags() { this.selectedTags = []; }
    clearSelectedStatuses() { this.selectedStatuses = []; }
    clearSelectedPeriods() { this.selectedPeriods = []; }

    clearVaultFilters() {
        this.searchValue = '';
        this.selectedTags = [];
        this.selectedStatuses = [];
        this.selectedPeriods = [];
        this.activeFilters = ['all-files'];
        this.selectedFile = null;
        this.exitBulkEditMode();
    }

    removeFilter(filterId) {
        const filterActions = {
            'tags': () => this.clearSelectedTags(),
            'status': () => this.clearSelectedStatuses(),
            'period': () => this.clearSelectedPeriods(),
            'default': () => this.removeActiveFilter(filterId)
        };

        (filterActions[filterId] || filterActions.default)();
        this.refetchFiles();
    }

    get sortedSelectedTags() {
        if (this.selectedTags.length === 0) return [];

        if (this.userTags.length > 0) {
            return this.selectedTags.map(selectedTag => {
                const matchingUserTag = this.userTags.find(userTag =>
                    compareItems(userTag, selectedTag, ['tag', 'name', 'id'])
                );
                return matchingUserTag || normalizeTag(selectedTag);
            }).filter(Boolean);
        }

        return this.selectedTags.map(normalizeTag);
    }

    get hasSelectedTags() {
        return this.selectedTags.length > 0;
    }
    get hasSelectedStatuses() {
        return this.selectedStatuses.length > 0;
    }
    get hasSelectedPeriods() {
        return this.selectedPeriods.length > 0;
    }

    get hasSearchValue() {
        return this.searchValue && this.searchValue.length > 0;
    }

    get predefinedTags() {
        return this.userTags.filter(tag => Object.keys(PREDEFINED_TAG_NAMES).includes(tag.id));
    }

    get customTags() {
        return this.userTags.filter(tag => !Object.keys(PREDEFINED_TAG_NAMES).includes(tag.id));
    }

    get allAvailableTags() {
        const tagsMap = new Map();
        this.userTags.forEach(tag => {
            tagsMap.set(tag.id, tag);
        });

        Object.entries(PREDEFINED_TAG_NAMES).forEach(([predefinedTagId, predefinedTagNameLabel]) => {
            tagsMap.set(predefinedTagId, {
                id: predefinedTagId,
                label: predefinedTagNameLabel,
                tag: predefinedTagId,
                name: predefinedTagId,
                isPredefined: true
            });
        });

        return Array.from(tagsMap.values());
    }

    get aggregatedSelectedPeriods() {
        if (this.selectedPeriods.length === 0) return [];

        const yearGroups = new Map();
        const nonYearPeriods = [];

        this.selectedPeriods.forEach(period => {
            const yearMatch = period.key?.match(/^(\d{4})-Q[1-4]$/) ||
                period.label?.match(/^Q[1-4]\s+(\d{4})$/) ||
                period.period?.match(/^Q[1-4]\s+(\d{4})$/);

            if (yearMatch) {
                const year = yearMatch[1];
                if (!yearGroups.has(year)) yearGroups.set(year, []);
                yearGroups.get(year).push(period);
            } else {
                nonYearPeriods.push(period);
            }
        });

        const result = [];
        yearGroups.forEach((quarters, year) => {
            if (quarters.length === 4) {
                result.push({ key: year, label: year, period: year, type: 'year' });
            } else {
                result.push(...quarters);
            }
        });

        result.push(...nonYearPeriods);
        return result;
    }

    get filesByPage() {
        return this.files.slice(((this.page - 1) * PAGE_SIZE), (this.page * PAGE_SIZE));
    }

    get mobileVisibleFiles() {
        return this.files.length;
    }

    get isMetadataPanelOpen() {
        return !!this.selectedFile;
    }

    get isAssetVaultTab() {
        return !!this.assetId;
    }

    get hasHandledFiles() {
        return (this.files.length > 0);
    }

    setSelectedFile(file) {
        this.selectedFile = file;
        if (file?.id) this.fetchVaultDetails(file.id);
    }

    clearSelectedFile() {
        this.selectedFile = null;
    }

    fetchVaultMetadata = async () => {
        try {
            this.isLoadingMetadata = true;
            this.vaultMetadata = await fetchVaultMetadata();
        } catch (error) {
            console.error('Error fetching vault metadata:', error);
        } finally {
            this.isLoadingMetadata = false;
        }
    }

    fetchUserTags = async () => {
        try {
            this.isLoadingTags = true;
            const tags = await fetchUserTags();
            this.userTags = tags.map(normalizeTag);
            return this.userTags;
        } catch (error) {
            console.error('Error fetching user tags:', error);
            return [];
        } finally {
            this.isLoadingTags = false;
        }
    }

    fetchUserPeriods = async () => {
        try {
            this.isLoadingPeriods = true;
            const periods = await fetchUserPeriods();
            this.userPeriods = transformPeriodsFromBackend(periods, this.hasNoPeriodFiles);
            return this.userPeriods;
        } catch (error) {
            console.error('Error fetching user periods:', error);
            return [];
        } finally {
            this.isLoadingPeriods = false;
        }
    }

    fetchVaultDetails = async (id) => {
        try {
            this.isLoadingVaultDetails = true;
            const vaultDetails = await fetchVaultById(id);
            const transformedDetails = normalizeVaultData(vaultDetails);

            if (this.selectedFile && this.selectedFile.id === id) {
                this.selectedFile = { ...this.selectedFile, ...transformedDetails };
            }

            return transformedDetails;
        } catch (error) {
            console.error('Error fetching vault details:', error);
            return null;
        } finally {
            this.isLoadingVaultDetails = false;
        }
    }

    updateSelectedVaultMetadata = async (metadata) => {
        if (!this.selectedFile?.id) return;

        try {
            const updatedVault = await updateVaultMetadata(this.selectedFile.id, metadata);
            const transformedVault = normalizeVaultData(updatedVault);

            this.selectedFile = { ...this.selectedFile, ...transformedVault };

            const fileIndex = this.files.findIndex(f => f.id === this.selectedFile.id);
            if (fileIndex >= 0) {
                this.files[fileIndex] = { ...this.files[fileIndex], ...transformedVault };
            }

            return transformedVault;
        } catch (error) {
            console.error('Error updating vault metadata:', error);
            throw error;
        }
    }

    downloadSelectedFile = async () => {
        if (!this.selectedFile?.id) return;

        try {
            const response = await downloadVaultFile(this.selectedFile.id);
            if (response.downloadUrl) {
                const fileName = this.selectedFile.originalName || this.selectedFile.displayName || 'download';
                downloadFromUrl(response.downloadUrl, fileName);
            }
            return response;
        } catch (error) {
            console.error('Error downloading file:', error);
            throw error;
        }
    }

    deleteSelectedFile = async () => {
        if (!this.selectedFile?.id) return;
        await this.deleteVault(this.selectedFile.id);
        await this.metadataStore.fetchData();
    }

    async deleteVaults(vaultIds) {
        if (!vaultIds?.length) return;

        const isSelectedFileDeleted = this.selectedFile && vaultIds.includes(this.selectedFile.id);

        await Promise.all(vaultIds.map(async (vaultId) => await deleteVaultFile(vaultId)));
        this.refetchFiles();
        await this.metadataStore.fetchData();

        if (isSelectedFileDeleted) {
            this.clearSelectedFile();
        }

        const pluralSuffix = vaultIds.length === 1 ? '' : 's';
        successNotification(`Successfully deleted ${vaultIds.length} file${pluralSuffix}`);
    }

    async deleteVault(vaultId) {
        if (!vaultId) return;

        try {
            const isSelectedFileDeleted = this.selectedFile && this.selectedFile.id === vaultId;
            await deleteVaultFile(vaultId);
            this.files = this.files.filter(f => f.id !== vaultId);
            this.selectedFilesForBulkEdit = this.selectedFilesForBulkEdit.filter(id => this.files.some(f => f.id === id));
            await this.metadataStore.fetchData();

            if (isSelectedFileDeleted) {
                this.clearSelectedFile();
            }

            successNotification("File was deleted successfully");
            return true;
        } catch (error) {
            console.error('Error deleting vault:', error);
            throw error;
        }
    }

    setFiles(files) { this.files = files; }
    setMeta(meta) { this.meta = meta; }
    setMainTableWidth(width) { this.mainTableWidth = width; }
    setActiveFilters(activeFilters) { this.activeFilters = activeFilters; }
    setSelectedTags(tags) { this.selectedTags = tags; }
    setSelectedStatuses(statuses) { this.selectedStatuses = statuses; }
    setSelectedPeriods(periods) { this.selectedPeriods = periods; }

    async setAssetId(assetId) {
        try {
            if (this.isUpdatingAssetId) {
                await new Promise(resolve => {
                    const check = () => {
                        if (!this.isUpdatingAssetId) {
                            resolve();
                        } else {
                            setTimeout(check, 50);
                        }
                    };
                    check();
                });
            }

            this.isUpdatingAssetId = true;

            if (this.assetId === assetId) return;

            this.assetId = assetId;
            await this.refetchFiles();

        } catch (error) {
            console.error('Error setting assetId:', error);
            throw error;
        } finally {
            this.isUpdatingAssetId = false;
        }
    }

    setVaultFormStep(vaultFormStep) { this.vaultFormStep = vaultFormStep; }
    setFilesData(filesData) { this.filesData = filesData; }

    addActiveFilter(filterId) {
        if (!this.activeFilters.includes(filterId)) {
            this.activeFilters = [...this.activeFilters, filterId];
        }
    }

    removeActiveFilter(filterId) {
        this.activeFilters = this.activeFilters.filter(id => id !== filterId);
    }

    exitBulkEditMode() {
        this.selectedFilesForBulkEdit = [];
    }

    addOrRemoveFileForBulkEdit(fileId) {
        if (this.selectedFilesForBulkEdit.includes(fileId)) {
            this.selectedFilesForBulkEdit = this.selectedFilesForBulkEdit.filter(id => id !== fileId);
        } else {
            if (this.selectedFile) {
                this.clearSelectedFile();
            }
            this.selectedFilesForBulkEdit.push(fileId);
        }
    }

    bulkEditToggleSelectAllFiles() {
        if (this.selectedFilesForBulkEdit.length > 0) {
            this.selectedFilesForBulkEdit = [];
        } else {
            if (this.selectedFile) {
                this.clearSelectedFile();
            }

            this.selectedFilesForBulkEdit = this.filesByPage.map(file => file.id);
        }
    }

    setSelectedAttributeToEdit(attribute) {
        this.selectedAttributeToEdit = attribute;
    }

    get hasActiveFilters() {
        return this.hasSelectedTags || this.hasSelectedStatuses || this.hasSelectedPeriods || this.hasSearchValue;
    }

    get isAllFilesSelectedForBulkEdit() {
        return this.filesByPage.length > 0 && this.selectedFilesForBulkEdit.length === this.filesByPage.length && this.filesByPage.every(file => this.selectedFilesForBulkEdit.includes(file.id));
    }

    get isPartialFilesSelectedForBulkEdit() {
        return this.selectedFilesForBulkEdit.length > 0 && this.selectedFilesForBulkEdit.length < this.filesByPage.length && this.filesByPage.some(file => this.selectedFilesForBulkEdit.includes(file.id));
    }

    get selectedFilesObjects() {
        return this.files.filter(file =>
            this.selectedFilesForBulkEdit.includes(file.id)
        );
    }

    get commonTagsAcrossSelectedFiles() {
        if (this.selectedFilesForBulkEdit.length === 0) return [];

        const selectedFiles = this.selectedFilesObjects;
        if (selectedFiles.length === 0) return [];

        const firstFileTags = selectedFiles[0]?.tags?.map(t => t.tag || t.name || t.label || t) || [];

        const commonTags = firstFileTags.filter(tag =>
            selectedFiles.every(file => {
                const fileTags = file?.tags?.map(t => t.tag || t.name || t.label || t) || [];
                return fileTags.includes(tag);
            })
        );

        return commonTags;
    }

    get commonPeriodsAcrossSelectedFiles() {
        if (this.selectedFilesForBulkEdit.length === 0) return null;

        const selectedFiles = this.selectedFilesObjects;
        if (selectedFiles.length === 0) return null;

        const firstFilePeriod = selectedFiles[0]?.periods?.[0]?.period;
        const commonPeriod = (firstFilePeriod && selectedFiles.every(file => file.periods?.[0]?.period === firstFilePeriod)) ? firstFilePeriod : null;
        return commonPeriod;
    }

    get commonLinkedItemsAcrossSelectedFiles() {
        if (this.selectedFilesForBulkEdit.length === 0) return [];

        const selectedFiles = this.selectedFilesObjects;
        if (selectedFiles.length === 0) return [];

        const firstFileItems = selectedFiles[0]?.relatedItems || selectedFiles[0]?.attachedToItems || [];

        const commonItems = firstFileItems.filter(item =>
            selectedFiles.every(file => {
                const fileItems = file?.relatedItems || file?.attachedToItems || [];
                return fileItems.some(fileItem => fileItem.id === item.id);
            })
        );

        return commonItems;
    }

    get isAllFilesFilterActive() {
        return !this.hasActiveFilters && (this.meta.totalVaultsInDB === this.meta.totalItems);
    }

    get isBulkEditMode() {
        return (this.selectedFilesForBulkEdit.length > 0);
    }

    get vaultsInProgressWithLinkedAssets() {
        return this.meta.vaultsInProgressWithLinkedAssets || 0;
    }

    async saveBulkEditChanges(valueToSave) {
        this.isLoadingBulkUpdate = true;

        try {
            this.valuesForBulkEditUndoAction = this.selectedFilesObjects.map(file => ({
                fileId: file.id,
                tags: file.tags?.map(t => t.tag || t.name || t.label || t) || [],
                periods: file.periods?.map(p => p.period || p.label || p.key || p) || [],
                linkedItems: (file.relatedItems || file.attachedToItems || []).map(item => item.id)
            }));

            const dataToSend = {
                vaultIds: this.selectedFilesForBulkEdit,
            };

            switch (this.selectedAttributeToEdit) {
                case 'tags':
                    const tags = Array.isArray(valueToSave) ? valueToSave : [valueToSave];
                    dataToSend.tags = {
                        tags: tags.filter(tag => tag && typeof tag === 'string'),
                        operation: 'replace'
                    };
                    break;
                case 'periods':
                    const periods = Array.isArray(valueToSave) ? valueToSave : [valueToSave];
                    dataToSend.periods = {
                        periods: periods.filter(period => period && typeof period === 'string'),
                        operation: 'replace'
                    };
                    break;
                case 'linkedItems':
                    const assetIds = Array.isArray(valueToSave) ? valueToSave : [valueToSave];
                    dataToSend.linkedItems = {
                        assetIds: assetIds.filter(id => id && typeof id === 'number'),
                        operation: 'replace'
                    };
                    break;
                default:
                    console.warn('Unknown attribute type:', this.selectedAttributeToEdit);
                    throw new Error(`Unknown attribute type: ${this.selectedAttributeToEdit}`);
            }

            const hasValidData = dataToSend.tags?.tags?.length > 0 ||
                dataToSend.periods?.periods?.length > 0 ||
                dataToSend.linkedItems?.assetIds?.length > 0;

            if (!hasValidData) {
                errorNotification('No valid data to update', { autoClose: 5000 });
                return;
            }

            const result = await bulkUpdateMetadata(dataToSend);

            this.numberOfBulkEditUpdatedItems = result.successCount;
            await this.refetchFiles();
            await this.fetchVaultMetadata();

            this.selectedFilesForBulkEdit = [];

            const attributeNames = {
                tags: 'tags',
                periods: 'periods',
                linkedItems: 'linked items'
            };

            successNotificationWithUndo(
                `Successfully updated ${attributeNames[this.selectedAttributeToEdit]} for ${this.numberOfBulkEditUpdatedItems} file${this.numberOfBulkEditUpdatedItems === 1 ? '' : 's'}`,
                () => { this.undoBulkEditChanges() },
                { autoClose: 5000 }
            );

        } catch (error) {
            console.error('Bulk edit error:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to update files';
            errorNotification(errorMessage, { autoClose: 5000 });
        } finally {
            this.isLoadingBulkUpdate = false;
        }
    }

    async undoBulkEditChanges() {
        try {
            const promises = this.valuesForBulkEditUndoAction.map(async (fileData) => {
                const updateData = {
                    tags: fileData.tags,
                    periods: fileData.periods,
                    assetIds: fileData.linkedItems
                };

                return updateVaultMetadata(fileData.fileId, updateData);
            });

            await Promise.all(promises);

            await this.refetchFiles();
            await this.fetchVaultMetadata();

            successNotification('Successfully undone bulk edit changes');

            this.selectedFilesForBulkEdit = this.valuesForBulkEditUndoAction.map(data => data.fileId);

        } catch (error) {
            console.error('Undo error:', error);
            errorNotification('Failed to undo changes', { autoClose: 5000 });
        }
    }

    async bulkDeleteFiles() {
        this.isLoadingBulkDelete = true;

        try {
            const isSelectedFileDeleted = this.selectedFile && this.selectedFilesForBulkEdit.includes(this.selectedFile.id);

            const dataToSend = {
                vaultIds: this.selectedFilesForBulkEdit,
                permanent: false
            };

            const result = await bulkDeleteFiles(dataToSend);

            this.numberOfBulkEditUpdatedItems = result.successCount;
            await this.refetchFiles();
            await this.metadataStore.fetchData();

            if (isSelectedFileDeleted) {
                this.clearSelectedFile();
            }

            this.selectedFilesForBulkEdit = [];

            const pluralSuffix = this.numberOfBulkEditUpdatedItems === 1 ? '' : 's';
            successNotification(
                `Successfully deleted ${this.numberOfBulkEditUpdatedItems} file${pluralSuffix}`,
                { autoClose: 5000 }
            );

        } catch (error) {
            console.error('Bulk delete error:', error);
            errorNotification('Failed to delete files', { autoClose: 5000 });
        } finally {
            this.isLoadingBulkDelete = false;
        }
    }

    async bulkDownloadFiles() {
        try {
            const dataToSend = {
                vaultIds: this.selectedFilesForBulkEdit
            };

            const result = await bulkDownloadUrls(dataToSend);

            if (result && result.results) {
                result.results.forEach(({ downloadUrl, fileName }) => {
                    downloadFromUrl(downloadUrl, fileName);
                });

                const pluralSuffix = this.selectedFilesForBulkEdit.length === 1 ? '' : 's';
                successNotification(
                    `Started downloading ${this.selectedFilesForBulkEdit.length} file${pluralSuffix}`,
                    { autoClose: 5000 }
                );
            }

        } catch (error) {
            console.error('Bulk download error:', error);
            errorNotification('Failed to download files', { autoClose: 5000 });
        }
    }

    refreshPeriodsIfNeeded() {
        if (this.userPeriods.length > 0) {
            const originalPeriods = this.userPeriods
                .filter(p => p.type !== 'no-period-files')
                .map(p => p.period);

            this.userPeriods = transformPeriodsFromBackend(originalPeriods, this.hasNoPeriodFiles);
        }
    }
}